import React, { useCallback } from 'react';
import { useAsyncValue, useRevalidator } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components
import { UserGroupsManagementTable } from '../../../../components/display/tables/formTables/managementTables/UserGroupsManagementTable';
import { AddUserGroupForm } from '../../../../components/forms/AddUserGroupForm';

// Types
import { GroupsManagementLoaderData, UserGroup } from './types';

// Styles
import { Container, GroupsSection } from './styles';

// GQL
import { client } from '../../../../gql/client';
import {
    CREATE_USER_GROUP,
    DELETE_USER_GROUP,
} from '../../../../gql/mutations/userGroups';

// Redux Actions
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../../components/modals/SuperModal/types';

// Utils
import { showSuccessToast, showBackendErrorToast } from '../../../../utils/toasts';

export default function GroupsManagementPage(): JSX.Element {
    const loaderData = useAsyncValue() as GroupsManagementLoaderData;
    const revalidator = useRevalidator();
    const dispatch = useDispatch();
    const { t } = useTranslation(['management']);

    const onReFetchData = useCallback(() => {
        revalidator.revalidate();
    }, [revalidator]);

    // Devuelve true si el alta salió bien (el formulario se limpia solo entonces).
    const onCreateGroup = async (name: string): Promise<boolean> => {
        try {
            await client.mutate({
                mutation: CREATE_USER_GROUP,
                variables: { name: name.trim() },
            });
            showSuccessToast(t('management:groups.created'));
            onReFetchData();
            return true;
        } catch (err) {
            showBackendErrorToast(err, t('management:groups.saveError'));
            return false;
        }
    };

    const handleDeleteGroup = useCallback(
        async (groupId: string) => {
            try {
                await client.mutate({
                    mutation: DELETE_USER_GROUP,
                    variables: { id: groupId },
                });
                showSuccessToast(t('management:groups.deleted'));
                onReFetchData();
            } catch (err) {
                showBackendErrorToast(err, t('management:groups.saveError'));
            }
        },
        [onReFetchData, t]
    );

    const onDeleteGroup = useCallback(
        (group: UserGroup) => {
            dispatch(
                openModal(modalIds.DELETE_USER_GROUP_MODAL, {
                    groupName: group.name,
                    onConfirmDelete: async () => {
                        await handleDeleteGroup(group.id);
                    },
                })
            );
        },
        [dispatch, handleDeleteGroup]
    );

    const onEditGroup = useCallback(
        (group: UserGroup) => {
            dispatch(
                openModal(modalIds.EDIT_USER_GROUP_MODAL, {
                    group,
                    groups: loaderData.groups,
                    appUsers: loaderData.appUsers,
                    onSuccess: onReFetchData,
                })
            );
        },
        [dispatch, loaderData.groups, loaderData.appUsers, onReFetchData]
    );

    return (
        <Container>
            <GroupsSection>
                <AddUserGroupForm onCreate={onCreateGroup} />
            </GroupsSection>
            <GroupsSection>
                <UserGroupsManagementTable
                    data={loaderData.groups}
                    onEditGroup={onEditGroup}
                    onDeleteGroup={onDeleteGroup}
                />
            </GroupsSection>
        </Container>
    );
}
